<template>
  <div class="notifications-page flex-1 flex flex-col">
    <div class="notifications-hero bg-primary text-white">
      <div class="hero-row">
        <div>
          <p class="hero-eyebrow">Live Monitoring</p>
          <h3 class="hero-title">GPS Alerts</h3>
        </div>
        <div class="hero-count-card">
          <p class="hero-count">{{ unreadNotif }}</p>
          <p class="hero-count-label">Unread</p>
        </div>
      </div>
    </div>

    <div class="notifications-list-wrap flex-1">
      <div class="list-toolbar">
        <div class="toolbar-left">
          <span class="toolbar-title">Alerts</span>
          <span class="toolbar-subtitle">Page {{ currentPage }} / {{ totalPages }}</span>
        </div>
        <vs-button
          size="small"
          type="border"
          color="primary"
          :disabled="unreadNotif <= 0"
          @click="markAllAsRead"
        >
          Mark all as read
        </vs-button>
      </div>

      <ul class="notifications-list">
        <li
          v-for="ntf in paginatedNotifications"
          :key="ntf.idx"
          class="notification-item"
          @click="openActionModal(ntf)"
        >
          <div class="notification-left">
            <div class="notification-icon-wrap">
              <feather-icon
                v-if="Number(ntf.actionTaken) === 1"
                icon="CheckCircleIcon"
                :svgClasses="['stroke-current h-5 w-5 text-success']"
              ></feather-icon>
              <feather-icon
                v-else
                icon="AlertTriangleIcon"
                :svgClasses="['stroke-current h-5 w-5 text-warning']"
              ></feather-icon>
            </div>
            <div class="notification-content">
              <span
                class="font-medium block notification-title"
                :class="[`text-${ntf.category}`]"
                >{{ ntf.title }}</span
              >
              <small
                v-if="Number(ntf.actionTaken) === 1"
                class="action-badge"
                >Action taken</small
              >
              <small
                v-else-if="Number(ntf.readStatus) === 0"
                class="read-badge"
                >Unread</small
              >
              <small
                v-if="!hasAttachmentLinks(ntf.meta2)"
                class="notification-message"
                >{{ ntf.msg }}</small
              >
              <ul v-else class="attachment-list">
                <li v-for="item in parseAttachmentLinks(ntf.meta2)" :key="item.url">
                  <vs-button
                    :color="item.type == 'excel' ? 'success' : 'danger'"
                    @click.stop="openLink(item.url)"
                    size="small"
                    type="flat"
                    :icon="item.type == 'excel' ? 'description' : 'picture_as_pdf'"
                    >{{ item.name }}</vs-button
                  >
                </li>
              </ul>
            </div>
          </div>
          <div class="notification-right">
            <small class="notification-time">{{ ntf.create_time }}</small>
            <vs-button
              v-if="Number(ntf.readStatus) === 0"
              size="small"
              type="flat"
              icon="done"
              color="success"
              @click.stop="markOneAsRead(ntf.idx)"
            >
              Mark read
            </vs-button>
          </div>
        </li>
      </ul>

      <NotificationActionModal
        v-model="actionModalOpen"
        :notification="selectedNotification"
        :notifications="selectedNotification ? [selectedNotification] : []"
        @saved="onActionSaved"
      />

      <div v-if="!unreadNotifications || unreadNotifications.length === 0" class="empty-state">
        <feather-icon
          icon="InboxIcon"
          :svgClasses="['stroke-current h-10 w-10 text-grey']"
        ></feather-icon>
        <p class="empty-title">No unread alerts</p>
        <p class="empty-subtitle">New alerts will appear here automatically.</p>
      </div>

      <div v-if="unreadNotifications && unreadNotifications.length > 0" class="pagination-wrap">
        <vs-button
          size="small"
          type="border"
          color="primary"
          :disabled="currentPage <= 1"
          @click="currentPage = currentPage - 1"
        >
          Previous
        </vs-button>
        <span class="pagination-text">{{ currentPage }} / {{ totalPages }}</span>
        <vs-button
          size="small"
          type="border"
          color="primary"
          :disabled="currentPage >= totalPages"
          @click="currentPage = currentPage + 1"
        >
          Next
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationActionModal from './NotificationActionModal.vue';

export default {
  components: {
    NotificationActionModal,
  },
  data() {
    return {
      userInfo: JSON.parse(localStorage.getItem('userInfo')),
      unreadNotif: Number(localStorage.getItem('unreadNotif') || 0),
      unreadNotifications: JSON.parse(
        localStorage.getItem('unreadNotifications') || '[]',
      ),
      currentPage: 1,
      pageSize: 10,
      actionModalOpen: false,
      selectedNotification: null,
    };
  },
  computed: {
    totalPages() {
      const count = Array.isArray(this.unreadNotifications)
        ? this.unreadNotifications.length
        : 0;
      return Math.max(1, Math.ceil(count / this.pageSize));
    },
    paginatedNotifications() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return (this.unreadNotifications || []).slice(start, end);
    },
  },
  mounted() {
    this.refreshNotifications();
  },
  methods: {
    async refreshNotifications() {
      try {
        const response = await this.$http.get(
          `personals/notifications/${this.userInfo.uid}`,
        );
        const payload = response && response.data ? response.data : {};
        this.unreadNotifications = Array.isArray(payload.allNotifications)
          ? payload.allNotifications.map((x) => ({
              ...x,
              readStatus: Number(x.readStatus || 0),
              actionTaken: Number(x.actionTaken || 0),
            }))
          : [];
        this.unreadNotif = this.unreadNotifications.filter(
          (x) => Number(x.readStatus) === 0,
        ).length;

        localStorage.setItem('unreadNotif', this.unreadNotif);
        localStorage.setItem(
          'unreadNotifications',
          JSON.stringify(this.unreadNotifications),
        );
      } catch (_error) {}
    },
    openActionModal(ntf) {
      if (!ntf || !ntf.idx) return;
      this.selectedNotification = ntf;
      this.actionModalOpen = true;
    },
    onActionSaved({ idx, latestAction }) {
      this.unreadNotifications = (this.unreadNotifications || []).map((item) =>
        item.idx === idx
          ? {
              ...item,
              actionTaken: 1,
              latestAction,
              readStatus: 1,
            }
          : item,
      );
      this.unreadNotif = this.unreadNotifications.filter(
        (x) => Number(x.readStatus) === 0,
      ).length;
      localStorage.setItem('unreadNotif', this.unreadNotif);
      localStorage.setItem(
        'unreadNotifications',
        JSON.stringify(this.unreadNotifications),
      );
    },
    markOneAsRead(idx) {
      this.unreadNotifications = (this.unreadNotifications || []).map((item) =>
        item.idx === idx ? { ...item, readStatus: 1 } : item,
      );
      this.unreadNotif = this.unreadNotifications.filter(
        (x) => Number(x.readStatus) === 0,
      ).length;
      localStorage.setItem('unreadNotif', this.unreadNotif);
      localStorage.setItem(
        'unreadNotifications',
        JSON.stringify(this.unreadNotifications),
      );
    },
    async markAllAsRead() {
      try {
        await this.$http.patch(`personals/notifications/${this.userInfo.uid}`);
      } catch (_error) {}
      this.unreadNotifications = (this.unreadNotifications || []).map((item) => ({
        ...item,
        readStatus: 1,
      }));
      this.unreadNotif = 0;
      localStorage.setItem('unreadNotif', this.unreadNotif);
      localStorage.setItem(
        'unreadNotifications',
        JSON.stringify(this.unreadNotifications),
      );
    },
    openLink(url) {
      window.open(url, '_blank');
    },
    parseAttachmentLinks(meta2) {
      if (!meta2) return [];
      try {
        const parsed = typeof meta2 === 'string' ? JSON.parse(meta2) : meta2;
        return Array.isArray(parsed) ? parsed : [];
      } catch (_error) {
        return [];
      }
    },
    hasAttachmentLinks(meta2) {
      return this.parseAttachmentLinks(meta2).length > 0;
    },
  },
};
</script>

<style scoped>
.notifications-page {
  background: #f7f8fc;
}

.notifications-hero {
  padding: 18px 20px;
  border-radius: 12px;
  margin: 14px;
  box-shadow: 0 8px 24px rgba(22, 55, 122, 0.2);
}

.hero-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hero-eyebrow {
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 4px;
}

.hero-title {
  color: #fff;
  margin: 0;
}

.hero-count-card {
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  min-width: 88px;
  text-align: center;
  padding: 8px 12px;
}

.hero-count {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.hero-count-label {
  font-size: 11px;
  margin: 0;
  opacity: 0.85;
}

.notifications-list-wrap {
  margin: 0 14px 14px;
  overflow: auto;
}

.list-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.toolbar-left {
  display: flex;
  flex-direction: column;
}

.toolbar-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3550;
}

.toolbar-subtitle {
  font-size: 12px;
  color: #8891a7;
}

.notifications-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notification-item {
  background: #fff;
  border: 1px solid #ebeef4;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.notification-item:hover {
  box-shadow: 0 6px 20px rgba(27, 45, 94, 0.08);
}

.notification-left {
  display: flex;
  align-items: flex-start;
  flex: 1;
}

.notification-icon-wrap {
  background: #fff7e8;
  border-radius: 9999px;
  padding: 8px;
  margin-right: 10px;
}

.notification-content {
  min-width: 0;
}

.notification-title {
  line-height: 1.3;
}

.read-badge {
  display: inline-block;
  margin-top: 5px;
  margin-bottom: 2px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #fff0d6;
  color: #b46a00;
  font-size: 11px;
  font-weight: 600;
}

.action-badge {
  display: inline-block;
  margin-top: 5px;
  margin-bottom: 2px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #e8f8ef;
  color: #15803d;
  font-size: 11px;
  font-weight: 600;
}

.notification-message {
  color: #59627a;
  display: block;
  margin-top: 4px;
}

.attachment-list {
  margin-top: 6px;
}

.notification-time {
  color: #8f97ab;
  margin-left: 10px;
  white-space: nowrap;
}

.notification-right {
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.empty-state {
  margin-top: 24px;
  text-align: center;
  color: #7d8497;
}

.empty-title {
  margin-top: 8px;
  font-weight: 600;
}

.empty-subtitle {
  font-size: 12px;
  opacity: 0.85;
}

.pagination-wrap {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.pagination-text {
  font-size: 12px;
  color: #6f7890;
  min-width: 56px;
  text-align: center;
}
</style>
